import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { PostgrestError } from '@supabase/supabase-js';
import type {
  Address,
  ContactUpdateData,
  SupplierUpdateData,
} from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

export const useUpdateSupplier = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<PostgrestError | null>(null);

  /**
   * Update any supplier fields
   */
  const updateSupplier = async (
    supplierId: string,
    data: SupplierUpdateData
  ) => {
    setLoading(true);
    setError(null);

    const { error: supabaseError } = await supabase
      .from('suppliers')
      .update({ ...data, updated_at: new Date().toISOString() })
      .eq('id', supplierId)
      .select();

    setLoading(false);
    if (supabaseError) {
      setError(supabaseError);
      return false;
    }
    return true;
  };

  /**
   * Update supplier address only
   */
  const updateSupplierAddress = async (
    addressId: string,
    newAddress: Partial<Address>
  ) => {
    const { error } = await supabase
      .from('addresses') // <-- Target the addresses table
      .update(newAddress)
      .eq('id', addressId); // <-- Filter by the Address ID

    return !error;
  };

  /**
   * Update supplier industries (replace all with new list)
   */
  const updateSupplierIndustries = async (
    supplierId: string,
    industryIds: string[]
  ) => {
    setLoading(true);
    setError(null);

    // Step 1: delete existing industries
    const { error: deleteError } = await supabase
      .from('supplier_industries')
      .delete()
      .eq('supplier_id', supplierId);

    if (deleteError) {
      setError(deleteError);
      setLoading(false);
      return false;
    }

    // Step 2: insert new industries if any
    if (industryIds.length > 0) {
      const { error: insertError } = await supabase
        .from('supplier_industries')
        .insert(
          industryIds.map((industryId) => ({
            supplier_id: supplierId,
            industry_id: industryId,
          }))
        );

      if (insertError) {
        setError(insertError);
        setLoading(false);
        return false;
      }
    }

    setLoading(false);
    return true;
  };

  /**
   * Update contact details (name, email, phone)
   */
  // const updateContact = async (
  //   contactId: string,
  //   contactData: ContactUpdateData
  // ) => {
  //   setLoading(true);
  //   setError(null);

  //   const { error: supabaseError } = await supabase
  //     .from('contacts')
  //     .update(contactData)
  //     .eq('id', contactId)
  //     .select();

  //   setLoading(false);
  //   if (supabaseError) {
  //     setError(supabaseError);
  //     return false;
  //   }
  //   return true;
  // };

  /**
   * Create or update a contact, and ensure supplier_contact linking is handled.
   */
  const saveContact = async (
    supplierId: string,
    contactId: string,
    contactData: ContactUpdateData
  ) => {
    setLoading(true);
    setError(null);

    try {
      // 1. Check if the contact already exists
      const { data: existing, error: checkError } = await supabase
        .from('contacts')
        .select('id')
        .eq('id', contactId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (!existing) {
        const { error: insertError } = await supabase.from('contacts').insert({
          id: contactId,
          ...contactData,
        });

        if (insertError) throw insertError;

        const { error: linkError } = await supabase
          .from('supplier_contact')
          .insert({
            supplier_id: supplierId,
            contact_id: contactId,
          });

        if (linkError) throw linkError;

        setLoading(false);
        return true;
      }

      const { error: updateError } = await supabase
        .from('contacts')
        .update(contactData)
        .eq('id', contactId);

      if (updateError) throw updateError;

      setLoading(false);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'contact.save' });
      setError(err);
      setLoading(false);
      return false;
    }
  };

  /**
   * Delete a contact from a supplier. If the contact is only linked to this supplier,
   * fully delete it from the contacts table. Otherwise only remove the link.
   */
  const deleteContact = async (supplierId: string, contactId: string) => {
    setLoading(true);
    setError(null);

    try {
      // 1. How many suppliers is this contact linked to?
      const { data: links, error: linkError } = await supabase
        .from('supplier_contact')
        .select('id')
        .eq('contact_id', contactId);

      if (linkError) throw linkError;

      const linkCount = links?.length ?? 0;

      // 2. Remove the specific link for this supplier
      const { error: unlinkError } = await supabase
        .from('supplier_contact')
        .delete()
        .eq('supplier_id', supplierId)
        .eq('contact_id', contactId);

      if (unlinkError) throw unlinkError;

      // 3. If this was the LAST link → delete the contact completely
      if (linkCount <= 1) {
        const { error: deleteError } = await supabase
          .from('contacts')
          .delete()
          .eq('id', contactId);

        if (deleteError) throw deleteError;
      }

      setLoading(false);
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureSupabaseError(err, { op: 'contact.delete' });
      setError(err);
      setLoading(false);
      return false;
    }
  };

  return {
    loading,
    error,
    updateSupplier,
    updateSupplierAddress,
    updateSupplierIndustries,
    saveContact,
    deleteContact,
  };
};
